import type { Router, RouteRecordRaw } from 'vue-router';

import { usePermissionStoreWithOut } from '@/store/modules/permission';

import { PageEnum } from '@/enums/pageEnum';
import { useUserStoreWithOut } from '@/store/modules/user';

import { PAGE_NOT_FOUND_ROUTE } from '@/router/routes/basic';

import { RootRoute } from '@/router/routes';
import { fetchPermmenu } from '@/fantasy/common/api/generated/user-center';
import { generatorDynamicRouter } from '@/fantasy/router/generator-router';
import { uniqBy } from 'lodash-es';
import axios from 'axios';

const LOGIN_PATH = PageEnum.BASE_LOGIN;

const ROOT_PATH = RootRoute.path;

const whitePathList: PageEnum[] = [LOGIN_PATH];

export function createPermissionGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const permissionStore = usePermissionStoreWithOut();
  router.beforeEach(async (to, from, next) => {
    if (
      from.path === ROOT_PATH &&
      to.path === PageEnum.BASE_HOME &&
      userStore.getUserInfo.homePath &&
      userStore.getUserInfo.homePath !== PageEnum.BASE_HOME
    ) {
      next(userStore.getUserInfo.homePath);
      return;
    }

    const token = userStore.getToken;

    // Whitelist can be directly entered
    if (whitePathList.includes(to.path as PageEnum)) {
      if (to.path === LOGIN_PATH && token) {
        const isSessionTimeout = userStore.getSessionTimeout;
        try {
          await userStore.afterLoginAction();
          if (!isSessionTimeout) {
            next((to.query?.redirect as string) || '/');
            return;
          }
        } catch {
          //
        }
      }
      next();
      return;
    }

    if (typeof __PUBLIC_ROUTES_URL__ === 'undefined' || !__PUBLIC_ROUTES_URL__) {
      // token or user does not exist
      if (!token) {
        // You can access without permission. You need to set the routing meta.ignoreAuth to true
        if (to.meta.ignoreAuth) {
          next();
          return;
        }

        // redirect login page
        const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
          path: LOGIN_PATH,
          replace: true,
        };
        if (to.fullPath) {
          redirectData.query = {
            ...redirectData.query,
            redirect: to.fullPath,
          };
        }
        next(redirectData);
        return;
      }

      // Jump to the 404 page after processing the login
      if (
        from.path === LOGIN_PATH &&
        to.name === PAGE_NOT_FOUND_ROUTE.name &&
        to.fullPath !== (userStore.getUserInfo.homePath || PageEnum.BASE_HOME)
      ) {
        next(userStore.getUserInfo.homePath || PageEnum.BASE_HOME);
        return;
      }
    }

    // 如果已添加动态路由则直接放行
    if (permissionStore.getIsDynamicAddedRoute) {
      next();
      return;
    }

    let permMenu;
    try {
      if (typeof __PUBLIC_ROUTES_URL__ !== 'undefined' && !!__PUBLIC_ROUTES_URL__) {
        // 微前端路由
        const res = await axios.get(__PUBLIC_ROUTES_URL__);
        permMenu = { menus: res.data.data, perms: [] };
      } else {
        // 获取用户信息如果上次获取时间为空
        if (userStore.getLastUpdateTime === 0) {
          await userStore.getUserInfoAction();
        }
        permMenu = await fetchPermmenu();
      }
    } catch (err) {
      console.error('获取权限菜单失败:', err);
      next();
      return;
    }
    // 类型转换确保兼容性
    const menus = uniqBy(permMenu.menus as Array<Record<string, any>>, (item) => item.id);
    // 生成路由
    const generatorResult = generatorDynamicRouter(menus as unknown as RouteRecordRaw[]);
    userStore.setMenus(
      (generatorResult.menus as any).filter((item) => 
        !item.meta?.hideInMenu
      )
    );
    userStore.setPerms(permMenu.perms);
    permissionStore.setPermCodeList(permMenu.perms);

    // const routes = await permissionStore.buildRoutesAction();

    // routes.forEach((route) => {__PUBLIC_ROUTES_URL__
    //   router.addRoute(route as unknown as RouteRecordRaw);
    // });

    // router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);

    permissionStore.setDynamicAddedRoute(true);

    // 处理动态路由添加后的重定向
    if (to.name === PAGE_NOT_FOUND_ROUTE.name || to.name === (PAGE_NOT_FOUND_ROUTE.name + 'Content')) {
      next({ path: to.fullPath, replace: true, query: to.query });
      return;
    }

    // 处理普通重定向
    const redirectPath = (from.query.redirect || to.path) as string;
    const redirect = decodeURIComponent(redirectPath);
    if (to.path !== redirect) {
      next({ path: redirect, replace: true });
      return;
    }
    next({ ...to, replace: true });
  });
}
